/**
 * useSecurityBrief — derives the editorial copy block at the top of the
 * Security Center from the active posture state.
 *
 * Mirrors `usePostureBrief` exactly. The hook is a thin memoization layer
 * around `composeSecurityBrief`, parameterized so the page can pass through
 * score + criticalCount + delta + topAction when they're available. The
 * function is pure — no API calls — so tenant isolation falls out for free
 * (the caller is responsible for passing the right tenant's data).
 */

import { useMemo } from 'react'
import { useMsal } from '@azure/msal-react'
import {
  composeSecurityBrief,
  type SecurityBrief,
  type SecurityTopAction,
} from '@/lib/security-brief'

export interface UseSecurityBriefArgs {
  /**
   * Active tenant ID. Combined with the local day to seed editorial variant
   * selection so the brief stays stable for that tenant across the day.
   */
  tenantId?: string
  /** Current Guardian Score composite (0-100) — same value the Command
   *  Center surfaces. Security Center re-presents the composite, it does
   *  not compute its own score. */
  score?: number | null
  /** Open SHALL-violation count — drives the weak-link clause. */
  criticalCount?: number
  /** 30-day score delta — drives the green pill and the headline modifier. */
  deltas?: { score?: number; days?: number }
  /** Top open SHALL violation (or other named risk) — informs the verdict
   *  + deck risk slot, and names the framework as the risk in the weak-link
   *  when there are 4+ critical items in play. */
  topAction?: SecurityTopAction
}

export function useSecurityBrief(args: UseSecurityBriefArgs = {}): SecurityBrief {
  const { accounts } = useMsal()
  const userEmail = accounts[0]?.username

  // Memoize on the value-shape rather than the object identity so the page
  // can rebuild `args` each render without retriggering composition.
  const deltaScore = args.deltas?.score
  const deltaDays = args.deltas?.days
  const topActionTitle = args.topAction?.title
  const topActionFramework = args.topAction?.framework
  const topActionCriticality = args.topAction?.criticality

  return useMemo(
    () =>
      composeSecurityBrief({
        userEmail,
        tenantId: args.tenantId,
        score: args.score,
        criticalCount: args.criticalCount,
        deltas:
          deltaScore !== undefined || deltaDays !== undefined
            ? { score: deltaScore, days: deltaDays }
            : undefined,
        topAction:
          topActionTitle || topActionFramework
            ? {
                title: topActionTitle ?? '',
                framework: topActionFramework,
                criticality: topActionCriticality,
              }
            : undefined,
      }),
    [
      userEmail,
      args.tenantId,
      args.score,
      args.criticalCount,
      deltaScore,
      deltaDays,
      topActionTitle,
      topActionFramework,
      topActionCriticality,
    ],
  )
}
